import { useDebounceFn } from '@vueuse/core';
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue';
import { computed, inject, ref, toValue, watch } from 'vue';

import type { Attachment, Invoice } from '@stuut/api/models';
import { toast } from '@ui/components/sonner';

import { stuut } from '@/api';
import { FileDropzoneKey } from '@/components/file-upload-dropzone';
import type { FileDropzoneProviderContext } from '@/components/file-upload-dropzone';
import { MAX_FILE_AMOUNT } from '@/constants/Attachments';

import type { AttachableEntity } from './useCreditMemoAttachments';

interface InvoiceAttachableResource<M extends AttachableEntity> {
  attachInvoice: (entity: M, invoiceId: string) => Promise<unknown>;
  removeAttachment: (entity: M, attachment: Attachment) => Promise<unknown>;
}

interface UseInvoiceAttachmentsOptions<M extends AttachableEntity> {
  disabled?: MaybeRefOrGetter<boolean>;
  entity: MaybeRefOrGetter<M | null | undefined>;
  partnerId: MaybeRefOrGetter<string | undefined>;
  relatedInvoices: MaybeRefOrGetter<Invoice[]>;
  resource: InvoiceAttachableResource<M>;
}

export interface InvoiceAttachments {
  allOtherAttached: ComputedRef<boolean>;
  allowedNumAttachments: ComputedRef<number>;
  allRelatedAttached: ComputedRef<boolean>;
  handleAttachAllInvoices: (list: Invoice[]) => Promise<void>;
  hasInvoicePdf: (invoice: Invoice) => boolean;
  isInvoiceAttached: (invoice: Invoice) => boolean;
  isInvoicePending: (invoice: Invoice) => boolean;
  otherInvoices: Ref<Invoice[]>;
  pendingInvoiceAttachments: Ref<string[]>;
  toggleInvoiceAttachment: (invoice: Invoice) => Promise<void>;
  updateInvoiceSearch: (term: string) => void;
}

export const filterInvoices = <T extends { number?: string | null }>(
  list: T[],
  term: string,
): T[] => {
  const lower = term.toLowerCase();
  return list.filter((i) => i.number?.toLowerCase()?.includes(lower));
};

export function useInvoiceAttachments<M extends AttachableEntity>(
  options: UseInvoiceAttachmentsOptions<M>,
): InvoiceAttachments {
  const otherInvoices = ref<Invoice[]>([]);
  const pendingInvoiceAttachments = ref<string[]>([]);
  const attachingAll = ref(false);

  const fileDropzone = inject<FileDropzoneProviderContext | undefined>(FileDropzoneKey, undefined);

  const allowedNumAttachments = computed<number>(
    () => MAX_FILE_AMOUNT - (toValue(options.entity)?.attachments?.length ?? 0),
  );

  const attachmentIdSet = computed<Set<string>>(
    () => new Set(toValue(options.entity)?.attachments?.map((a) => a.id) ?? []),
  );

  const isInvoiceAttached = (invoice: Invoice): boolean =>
    !!invoice.pdf_id && attachmentIdSet.value.has(invoice.pdf_id);

  const isInvoicePending = (invoice: Invoice): boolean =>
    pendingInvoiceAttachments.value.includes(invoice.id);

  const hasInvoicePdf = (invoice: Invoice): boolean => !!invoice.pdf_id;

  const allRelatedAttached = computed<boolean>(() => {
    const attachable = toValue(options.relatedInvoices).filter(hasInvoicePdf);
    if (!attachable.length) return false;
    return attachable.every(isInvoiceAttached);
  });

  const allOtherAttached = computed<boolean>(() => {
    const attachable = otherInvoices.value.filter(hasInvoicePdf);
    if (!attachable.length) return false;
    return attachable.every(isInvoiceAttached);
  });

  const fetchInvoices = async (search = '') => {
    try {
      const partnerId = toValue(options.partnerId);
      const response = await stuut.invoices.list({
        params: {
          partner: partnerId,
          search,
          status: ['open'],
        },
      });

      const relatedIds = new Set(
        toValue(options.relatedInvoices)
          .map((inv) => inv.id)
          .filter(Boolean),
      );

      otherInvoices.value = (response ?? []).filter((inv) => !relatedIds.has(inv.id));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load invoices.');
    }
  };

  const toggleInvoiceAttachment = async (invoice: Invoice): Promise<void> => {
    if (toValue(options.disabled)) return;

    const entity = toValue(options.entity);
    if (!entity) return;

    const attachment = entity.attachments?.find((att) => att.id === invoice?.pdf_id);

    if (!attachment && allowedNumAttachments.value <= 0) {
      toast.error(`You may only attach up to ${MAX_FILE_AMOUNT} files.`);
      return;
    }

    pendingInvoiceAttachments.value.push(invoice.id);

    try {
      if (attachment) {
        await options.resource.removeAttachment(entity, attachment);
        fileDropzone?.removeFileAttachment(attachment);
      } else {
        await options.resource.attachInvoice(entity, invoice.id);
      }
    } catch (err) {
      console.error(err);
      toast.error(attachment ? 'Failed to detach invoice.' : 'Failed to attach invoice.');
    } finally {
      pendingInvoiceAttachments.value = pendingInvoiceAttachments.value.filter(
        (id) => id !== invoice.id,
      );
    }
  };

  const handleAttachAllInvoices = async (invoiceList: Invoice[]) => {
    if (attachingAll.value || toValue(options.disabled)) return;
    attachingAll.value = true;

    try {
      const attachable = invoiceList.filter(hasInvoicePdf);
      const allAttached = attachable.every(isInvoiceAttached);
      const targets = allAttached
        ? attachable.filter(isInvoiceAttached)
        : attachable.filter((inv) => !isInvoiceAttached(inv));

      for (const invoice of targets) {
        await toggleInvoiceAttachment(invoice);
      }
    } finally {
      attachingAll.value = false;
    }
  };

  const updateInvoiceSearch = useDebounceFn((term: string) => {
    void fetchInvoices(term?.trim()?.toLowerCase() ?? '');
  }, 400);

  watch(
    () => toValue(options.partnerId),
    (id) => {
      if (!id) return;
      void fetchInvoices();
    },
    { immediate: true },
  );

  return {
    allOtherAttached,
    allRelatedAttached,
    allowedNumAttachments,
    handleAttachAllInvoices,
    hasInvoicePdf,
    isInvoiceAttached,
    isInvoicePending,
    otherInvoices,
    pendingInvoiceAttachments,
    toggleInvoiceAttachment,
    updateInvoiceSearch,
  };
}
